import { create } from 'zustand';
import { settings } from '../lib/settings';
import api from '../lib/api';
import { getCookie, setCookie, removeCookie } from '../lib/cookies';
// @ts-ignore
import sign from 'jwt-encode';
import { User, AuthResponse } from '../types';
import Cookies from 'js-cookie';

// Interface for block information
interface BlockInfo {
  message: string;
  block_time_remaining: number;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  blockInfo: BlockInfo | null;
  login: (email: string, password: string, recaptchaToken?: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<string>;
  clearBlockInfo: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  blockInfo: null,

  login: async (email: string, password: string, recaptchaToken?: string) => {
    try {
      set({ isLoading: true, error: null, blockInfo: null });
      const response = await api.post(settings.api.auth.login, {
        email,
        password,
        recaptcha_token: recaptchaToken || ''
      });

      if (response.data) {
        const { access_token, refresh_token, ...userData } = response.data;

        // Store tokens
        setCookie(settings.cookies.access_token, access_token);
        setCookie(settings.cookies.refresh_token, refresh_token);

        // Store user data
        set({
          user: userData,
          isAuthenticated: true,
          error: null
        });

        setCookie(settings.cookies.user, JSON.stringify(userData));

        if (userData.organization) {
          // Make sure we save all organization fields, including created_at
          setCookie(settings.cookies.organization, JSON.stringify(userData.organization));
        }

        return response.data;
      }
      throw new Error("Invalid response from server");
    } catch (err: any) {
      // Check for IP blocking (429 Too Many Requests)
      if (err.response && err.response.status === 429) {
        const responseData = err.response.data;
        console.log('429 error response data:', responseData);
        
        // Correctly extract data from the response, taking into account the nested detail field
        let blockData: BlockInfo | null = null;
        
        if (responseData && typeof responseData === 'object') {
          // Check for the presence of data in the detail structure (as returned by the server)
          if (responseData.detail && 
              typeof responseData.detail === 'object' && 
              'block_time_remaining' in responseData.detail) {
            
            blockData = {
              message: responseData.detail.message || "Too many login attempts. Your IP is temporarily blocked.",
              block_time_remaining: responseData.detail.block_time_remaining || 0
            };
          } 
          // Also check for data in the root object for backward compatibility
          else if ('block_time_remaining' in responseData) {
            blockData = {
              message: responseData.message || "Too many login attempts. Your IP is temporarily blocked.",
              block_time_remaining: responseData.block_time_remaining || 0
            };
          }
        }
        
        if (blockData) {
          console.log('Block info extracted:', blockData);
          set({ 
            error: blockData.message, 
            blockInfo: blockData,
            isAuthenticated: false, 
            user: null 
          });
          
          // Start block countdown timer
          let timerId: ReturnType<typeof setTimeout>;
          
          const startCountdown = () => {
            // Clear any existing timer
            if (timerId) clearTimeout(timerId);
            
            const currentState = get();
            
            // If we have block info with remaining time, continue countdown
            if (currentState.blockInfo && currentState.blockInfo.block_time_remaining > 0) {
              timerId = setTimeout(() => {
                const newState = get();
                
                // Make sure we still have block info
                if (newState.blockInfo && newState.blockInfo.block_time_remaining > 0) {
                  const newRemainingTime = newState.blockInfo.block_time_remaining - 1;
                  
                  if (newRemainingTime <= 0) {
                    // If countdown has finished, clear block info
                    set({ 
                      blockInfo: null,
                      error: null
                    });
                  } else {
                    // Update the countdown
                    set({ 
                      blockInfo: {
                        ...newState.blockInfo,
                        block_time_remaining: newRemainingTime
                      }
                    });
                    
                    // Continue the countdown
                    startCountdown();
                  }
                }
              }, 1000);
            }
          };
          
          // Start the countdown
          startCountdown();
        } else {
          console.log('No block data found in response, structure:', JSON.stringify(responseData));
          // Generic rate limiting message
          set({ 
            error: "Too many login attempts. Please try again later.", 
            blockInfo: null,
            isAuthenticated: false, 
            user: null 
          });
        }
      } else {
        // Handle other errors
        const errorMessage = err instanceof Error ? err.message : "An error occurred during login";
        set({ error: errorMessage, blockInfo: null, isAuthenticated: false, user: null });
      }
      
      console.error("Login error:", err);
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true });
      await api.post(settings.api.auth.logout);
      set({
        user: null,
        isAuthenticated: false,
        error: null,
        blockInfo: null
      });

      // Clear all auth-related cookies
      Cookies.remove(settings.cookies.access_token);
      Cookies.remove(settings.cookies.refresh_token);
      Cookies.remove(settings.cookies.user);
      Cookies.remove(settings.cookies.organization);
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  refreshToken: async () => {
    try {
      const refresh_token = getCookie(settings.cookies.refresh_token);
      if (!refresh_token) {
        throw new Error("No refresh token available");
      }

      const response = await api.post(settings.api.auth.refresh, {
        refresh_token,
      });

      if (response.data?.access_token) {
        setCookie(settings.cookies.access_token, response.data.access_token);
        return response.data.access_token;
      }

      throw new Error("Failed to refresh token");
    } catch (error) {
      console.error("Token refresh error:", error);
      // If refresh fails, logout
      await useAuthStore.getState().logout();
      throw error;
    }
  },

  checkAuth: async () => {
    try {
      set({ isLoading: true });
      const response = await api.get(settings.api.auth.check);

      if (response.data) {
        set({
          user: response.data,
          isAuthenticated: true,
          error: null
        });

        setCookie(settings.cookies.user, JSON.stringify(response.data));
        if (response.data.organization) {
          setCookie(settings.cookies.organization, JSON.stringify(response.data.organization));
        }
      }
    } catch (error) {
      console.error("Auth check error:", error);
      set({
        user: null,
        isAuthenticated: false,
        error: "Authentication check failed"
      });
      removeCookie(settings.cookies.access_token);
      removeCookie(settings.cookies.refresh_token);
      removeCookie(settings.cookies.user);
      removeCookie(settings.cookies.organization);
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Add method to manually clear block info if needed
  clearBlockInfo: () => {
    set({ 
      blockInfo: null,
      error: null
    });
  }
}));
